// src/components/union-settings/banks/UnionBankDiscountDialog.tsx
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  Portal,
  Text,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { LuSave, LuTrash2 } from 'react-icons/lu'

export default function UnionBankDiscountDialog({
  open,
  onOpenChange,
  loading, // fetching current discount
  currentPercentage,
  onSubmit,
  onDelete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  loading?: boolean
  currentPercentage: number | null
  onSubmit: (pct: number) => Promise<void>
  onDelete?: () => Promise<void>
}) {
  const [saving, setSaving] = useState(false)
  const [value, setValue] = useState('')
  const [touched, setTouched] = useState(false)

  const busy = !!loading || saving

  useEffect(() => {
    if (open) {
      setValue(currentPercentage != null ? String(currentPercentage) : '')
      setTouched(false)
    }
  }, [open, currentPercentage])

  const error = useMemo(() => {
    if (!touched) return null
    const n = Number(value)
    if (value.trim().length === 0) return 'Bitte Rabatt eingeben.'
    if (Number.isNaN(n)) return 'Bitte eine Zahl eingeben.'
    if (n < 0 || n > 100) return 'Rabatt muss zwischen 0 und 100 liegen.'
    return null
  }, [touched, value])

  const handleSave = async () => {
    setTouched(true)
    if (error) return

    const n = Number(value)
    setSaving(true)
    try {
      await onSubmit(n)
      onOpenChange(false)
    } catch (e: any) {
      if (e?.code === 'INVALID_DISCOUNT_RATE') {
        toast.error('Rabatt ist ungültig', 'Bitte geben Sie einen gültigen Prozentsatz ein.')
      } else {
        toast.error(
          'Rabatt konnte nicht gespeichert werden',
          'Bitte führen Sie den Vorgang erneut aus.'
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete) return
    setSaving(true)
    try {
      await onDelete()
      onOpenChange(false)
    } catch {
      toast.error(
        'Rabatt konnte nicht entfernt werden',
        'Bitte führen Sie den Vorgang erneut aus.'
      )
    } finally {
      setSaving(false)
    }
  }

  const currentLabel = currentPercentage != null ? `${currentPercentage} %` : '—'
  const canDelete = currentPercentage != null && !!onDelete

  return (
    <Dialog.Root open={open} onOpenChange={e => onOpenChange(e.open)} size="md">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            rounded="2xl"
            maxW="3xl"
            shadow="0px 8px 16px 0px gray.muted;"
            overflow="hidden"
          >
            <Dialog.Header py={6} px={8}>
              <Flex justify="space-between" align="center">
                <Dialog.Title fontSize="xl" fontWeight="medium">
                  Rabatt festlegen
                </Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton
                    size="sm"
                    mt="4"
                    mr="4"
                    borderWidth="1px"
                    borderColor="border.muted"
                    disabled={busy}
                  />
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Header>

            <Dialog.Body px={6} pt={4} pb={6}>
              <Box borderWidth="1px" borderColor="border.muted" rounded="xl" px={4} py={4}>
                <Flex gap={10} align="flex-start">
                  <Box minW="130px">
                    <Text fontSize="sm" fontWeight="semibold" mb={3}>
                      Aktueller Rabatt
                    </Text>
                    <Text fontSize="xl" fontWeight="semibold" color="green.solid" lineHeight="1">
                      {loading ? 'Lädt…' : currentLabel}
                    </Text>

                    {canDelete && (
                      <Button
                        mt={4}
                        size="xs"
                        variant="outline"
                        colorPalette="red"
                        onClick={handleDelete}
                        loading={saving}
                        disabled={!!loading}
                      >
                        <LuTrash2 />
                        Rabatt entfernen
                      </Button>
                    )}
                  </Box>

                  <Box flex="1">
                    <Field.Root invalid={!!error}>
                      <Field.Label fontSize="sm" fontWeight="semibold">
                        Rabatt eintragen{' '}
                        <Text as="span" color="red.fg" fontWeight="normal">
                          *
                        </Text>
                      </Field.Label>

                      <Input
                        mt={2}
                        fontSize="sm"
                        value={value}
                        placeholder="Rabatt in Prozent"
                        onChange={e => setValue(e.target.value)}
                        onBlur={() => setTouched(true)}
                        disabled={busy}
                      />

                      <Field.ErrorText mt={2}>{error ?? ''}</Field.ErrorText>
                    </Field.Root>
                  </Box>
                </Flex>
              </Box>
            </Dialog.Body>

            <Dialog.Footer px={6} pb={6} pt={0}>
              <Flex w="full" justify="flex-end">
                <Button
                  size="md"
                  variant="solid"
                  fontSize="sm"
                  bg="blue.solid"
                  _hover={{ opacity: 0.9 }}
                  loading={saving}
                  disabled={!!loading}
                  onClick={handleSave}
                >
                  <LuSave />
                  Rabatt festlegen
                </Button>
              </Flex>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
